import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { AttachmentsCard } from '../../components/AttachmentsCard';
import { Alert, Badge, Button, Card, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import {
  formatDate,
  OPEN_STATUSES,
  type Priority,
  PRIORITIES,
  priorityName,
  type RequestDetail,
  STAGES,
  type Stage,
  stageName,
  statusName,
  statusTone,
  useRequest,
  useRequestForm,
} from './requests-data';

/** Detalle de una solicitud de proyecto: qué se pide, en qué etapa está, quién decidió qué y, si toca, la decisión. */
export function RequestDetailPage() {
  const { number = '' } = useParams();
  const location = useLocation();
  const request = useRequest(number);
  const [notice, setNotice] = useState<string | null>(null);

  if (!request.data) return request.error ? <Alert>{errorMessage(request.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const r = request.data;
  const state = location.state as { created?: boolean; failedFiles?: string[] } | null;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/proyectos/solicitudes" className="text-sm text-marca hover:underline">
          ← {t('sp.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          <span className="mr-2 font-mono text-base text-slate-500">{r.code}</span>
          {r.title}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={statusTone(r.status)}>{statusName(r.status)}</Badge>
          {r.priority && <span className="text-slate-600">· {t('sp.prioridad_interna', { prioridad: priorityName(r.priority) })}</span>}
          <span className="text-slate-500">· {r.organization}</span>
        </div>
      </div>
      {state?.created && <Alert tone="success">{t('sp.creada', { codigo: r.code })}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {!!state?.failedFiles?.length && <Alert tone="warning">{t('sp.adjuntos_fallidos', { archivos: state.failedFiles.join(', ') })}</Alert>}
      {r.project && !notice && (
        <Alert tone="success">
          {t('sp.proyecto_creado', { codigo: r.project.code })}{' '}
          <Link to={`/proyectos/${r.project.number}`} className="font-medium underline">
            {t('sp.ver_proyecto')}
          </Link>
        </Alert>
      )}

      <Stepper request={r} />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <Card title={t('sp.campo_problema')}>
            <p className="whitespace-pre-wrap text-sm text-slate-800">{r.description}</p>
          </Card>
          <Card title={t('sp.campo_beneficio')}>
            <p className="whitespace-pre-wrap text-sm text-slate-800">{r.expectedBenefit}</p>
          </Card>
          <Card title={t('sp.decisiones')}>
            {r.approvals.length === 0 ? (
              <p className="text-sm text-slate-500">{t('sp.sin_decisiones')}</p>
            ) : (
              <ol className="space-y-3" aria-label={t('sp.decisiones')}>
                {r.approvals.map((a) => (
                  <li key={`${a.stage}-${a.decidedAt}`} className={`rounded-md border p-3 text-sm ${a.approved ? 'border-slate-200' : 'border-red-200 bg-red-50'}`}>
                    <p className="font-medium text-slate-900">
                      {a.approved ? t('sp.decision_aprobo', { quien: a.decidedByName ?? '—', etapa: stageName(a.stage) }) : t('sp.decision_rechazo', { quien: a.decidedByName ?? '—', etapa: stageName(a.stage) })}
                    </p>
                    <p className="text-xs text-slate-500">{formatDate(a.decidedAt)}</p>
                    {a.reason && <p className="mt-1 whitespace-pre-wrap text-slate-700">{a.reason}</p>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
        <div className="space-y-4">
          {/* Una por etapa: si quien decide también tiene la siguiente, el panel vuelve a empezar. */}
          {r.can.decide && <DecisionCard key={r.status} request={r} onDone={setNotice} />}
          <Card variant="aside" title={t('sp.datos')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{t('sp.col_solicitante')}</dt>
              <dd>{r.requester ?? '—'}</dd>
              <dt className="text-slate-500">{t('sp.jefe')}</dt>
              <dd>{r.manager ?? <span className="text-slate-400">{t('sp.sin_jefe_corto')}</span>}</dd>
              <dt className="text-slate-500">{t('sp.campo_area')}</dt>
              <dd>{r.affectedArea ?? '—'}</dd>
              <dt className="text-slate-500">{t('sp.campo_urgencia')}</dt>
              <dd>{priorityName(r.perceivedUrgency)}</dd>
              <dt className="text-slate-500">{t('sp.campo_fecha')}</dt>
              <dd>{r.idealDate ? formatDate(r.idealDate) : '—'}</dd>
              <dt className="text-slate-500">{t('sp.tipo')}</dt>
              <dd>{r.projectType ?? <span className="text-slate-400">{t('sp.tipo_pendiente')}</span>}</dd>
              <dt className="text-slate-500">{t('sp.col_fecha')}</dt>
              <dd>{formatDate(r.createdAt)}</dd>
            </dl>
          </Card>
          <AttachmentsCard entity="project_request" entityId={r.id} canUpload={r.can.attach} canManage={r.can.manageAttachments} help={t('sp.adjuntos_ayuda')} />
          {r.can.withdraw && <WithdrawCard request={r} />}
        </div>
      </div>
    </div>
  );
}

/** Etapas de la aprobación (guía 7.2): jefe directo → viabilidad con IT → Gerente de IT. */
function Stepper({ request: r }: { request: RequestDetail }) {
  const rejectedAt = r.status === 'rechazada' ? r.approvals.find((a) => !a.approved)?.stage : undefined;
  const current = OPEN_STATUSES.includes(r.status) ? (r.status as Stage) : undefined;
  const passed = (s: Stage) => r.approvals.some((a) => a.stage === s && a.approved);
  return (
    <ol aria-label={t('sp.etapas')} className="flex flex-wrap items-center gap-2 text-sm">
      {STAGES.map((s, i) => {
        const state = rejectedAt === s ? 'rechazada' : passed(s) ? 'aprobada' : current === s ? 'actual' : 'pendiente';
        const style = {
          aprobada: 'border-green-200 bg-green-50 text-green-900',
          rechazada: 'border-red-200 bg-red-50 text-red-800',
          actual: 'border-amber-300 bg-amber-50 font-medium text-amber-900',
          pendiente: 'border-slate-200 bg-white text-slate-500',
        }[state];
        return (
          <li key={s} className="flex items-center gap-2">
            {i > 0 && <span className="text-slate-300" aria-hidden>→</span>}
            <span className={`rounded-full border px-3 py-1 ${style}`} aria-current={state === 'actual' ? 'step' : undefined}>
              {stageName(s)}
              <span className="sr-only"> ({t(`sp.paso_${state}` as 'sp.paso_actual')})</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

interface HighPriorityConflict {
  code: 'high_priority_exists';
  message: string;
  projects: Array<{ number: number; code: string; name: string }>;
}

/** Decisión de la etapa actual. El jefe ajusta la prioridad; IT elige el tipo; el Gerente de IT, además, el responsable. */
function DecisionCard({ request: r, onDone }: { request: RequestDetail; onDone: (message: string) => void }) {
  const qc = useQueryClient();
  const form = useRequestForm();
  const stage = r.status as Stage;
  const owners = useQuery({
    queryKey: ['solicitudes', 'responsables', r.number],
    queryFn: () => api<Array<{ id: string; fullName: string }>>(`/api/proyectos/solicitudes/${r.number}/responsables`),
    enabled: stage === 'gerente',
  });
  const [priority, setPriority] = useState<Priority>(r.priority ?? 'media');
  const [typeId, setTypeId] = useState(r.projectTypeId ?? '');
  const [ownerId, setOwnerId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<HighPriorityConflict | null>(null);

  async function decide(approved: boolean, confirmHighPriority = false) {
    setBusy(true);
    setError(null);
    try {
      const body = {
        approved,
        reason: reason.trim() || null,
        ...(approved && r.can.setPriority ? { priority } : {}),
        ...(approved && r.can.chooseType && typeId ? { projectTypeId: typeId } : {}),
        ...(approved && stage === 'gerente' && ownerId ? { ownerId } : {}),
        confirmHighPriority,
      };
      const res = await api<{ status: string; project: { code: string } | null }>(`/api/proyectos/solicitudes/${r.number}/decision`, { json: body });
      setConflict(null);
      onDone(!approved ? t('sp.rechazada_ok') : res.project ? t('sp.proyecto_creado', { codigo: res.project.code }) : t('sp.aprobada_ok'));
      await qc.invalidateQueries({ queryKey: ['solicitudes'] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<HighPriorityConflict> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'high_priority_exists') setConflict(body as HighPriorityConflict);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('sp.decidir', { etapa: stageName(stage) })}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{t(`sp.decidir_ayuda_${stage}` as 'sp.decidir_ayuda_jefe')}</p>
        {error && <Alert>{error}</Alert>}
        {r.can.setPriority && (
          <Select id="sp-prioridad" label={t('sp.prioridad')} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {priorityName(p)}
              </option>
            ))}
          </Select>
        )}
        {r.can.chooseType && (
          <Select id="sp-tipo" label={t('sp.tipo')} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t('sp.elegir')}</option>
            {form.data?.projectTypes?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        {stage === 'gerente' && (
          <Select id="sp-responsable" label={t('sp.responsable')} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">{t('sp.sin_responsable')}</option>
            {owners.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.fullName}
              </option>
            ))}
          </Select>
        )}
        <Textarea id="sp-motivo" label={t('sp.motivo')} rows={3} maxLength={2_000} value={reason} onChange={(e) => setReason(e.target.value)} />
        {conflict ? (
          <Alert tone="warning">
            <p>{conflict.message}</p>
            <ul className="mt-1 list-disc pl-5">
              {conflict.projects.map((p) => (
                <li key={p.number}>
                  {p.code} · {p.name}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button disabled={busy} onClick={() => void decide(true, true)}>
                {t('sp.confirmar_alta')}
              </Button>
              <Button variant="secondary" onClick={() => setConflict(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || (stage === 'viabilidad' && !typeId)} onClick={() => void decide(true)}>
              {stage === 'gerente' ? t('sp.aprobar_final') : t('sp.aprobar')}
            </Button>
            <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => void decide(false)}>
              {t('sp.rechazar')}
            </Button>
          </div>
        )}
        {!reason.trim() && <p className="text-xs text-slate-500">{t('sp.motivo_ayuda')}</p>}
      </div>
    </Card>
  );
}

function WithdrawCard({ request: r }: { request: RequestDetail }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function withdraw() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/proyectos/solicitudes/${r.number}/retirar`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['solicitudes'] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <Card>
      <div className="space-y-2 text-sm">
        {error && <Alert>{error}</Alert>}
        {confirming ? (
          <Alert tone="warning">
            <p>{t('sp.retirar_confirmar')}</p>
            <div className="mt-2 flex gap-2">
              <Button variant="danger" disabled={busy} onClick={() => void withdraw()}>
                {t('sp.si_retirar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('comun.volver')}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <p className="text-slate-600">{t('sp.retirar_ayuda')}</p>
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('sp.retirar')}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
